export const DASH_META_LISTEN_WEBSOCKET_PATH = '/api/ai-search/listen';

/**
 * WebSocket URL for the dash-meta Deepgram listen proxy.
 * Uses the URL API so host/port are correct; when `dashMetaRoot` is empty,
 * uses the current page origin (same behavior as relative `/api/...` fetch
 * through the dev proxy).
 */
export function getDashMetaListenWebSocketUrl(dashMetaRoot: string): string {
  const root = dashMetaRoot.trim();
  if (root === '') {
    if (typeof window === 'undefined') {
      return `ws://localhost${DASH_META_LISTEN_WEBSOCKET_PATH}`;
    }
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${window.location.host}${DASH_META_LISTEN_WEBSOCKET_PATH}`;
  }

  let baseUrl: URL;
  try {
    baseUrl = new URL(root.endsWith('/') ? root : `${root}/`);
  } catch {
    if (typeof window === 'undefined') {
      return `ws://localhost${DASH_META_LISTEN_WEBSOCKET_PATH}`;
    }
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${window.location.host}${DASH_META_LISTEN_WEBSOCKET_PATH}`;
  }

  const wsProtocol = baseUrl.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProtocol}//${baseUrl.host}${DASH_META_LISTEN_WEBSOCKET_PATH}`;
}

export type DeepgramTranscriptMessage = {
  type?: string;
  is_final?: boolean;
  speech_final?: boolean;
  channel?: {
    alternatives?: Array<{ transcript?: string }>;
  };
};

const MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
] as const;

export function getSupportedMimeType(): string | undefined {
  for (const type of MIME_TYPES) {
    if (
      typeof MediaRecorder !== 'undefined' &&
      MediaRecorder.isTypeSupported(type)
    ) {
      return type;
    }
  }
  return undefined;
}

export interface LastSentTranscript {
  text: string;
  at: number;
}

const DEFAULT_DEDUP_MS = 200;

export interface FinalTranscriptResult {
  text: string;
  speechFinal: boolean;
}

/**
 * Parses raw Deepgram message JSON and returns the final transcript text if
 * it should be emitted (is_final, has text, and passes dedup window).
 * Also returns whether Deepgram marked this as end-of-utterance (speech_final).
 * Returns null if the message should be ignored.
 */
export function parseFinalTranscript(
  raw: string,
  lastSent: LastSentTranscript,
  dedupWindowMs: number = DEFAULT_DEDUP_MS
): FinalTranscriptResult | null {
  try {
    const data = JSON.parse(raw) as DeepgramTranscriptMessage;
    if (data.is_final !== true || !data.channel?.alternatives?.[0]) {
      return null;
    }
    const text = data.channel.alternatives[0].transcript?.trim();
    if (!text) return null;
    const now = Date.now();
    if (text === lastSent.text && now - lastSent.at < dedupWindowMs) {
      return null;
    }
    return {
      text,
      speechFinal: data.speech_final === true,
    };
  } catch {
    return null;
  }
}
